/**
 * App state cache.
 *
 * D1 is now the source of truth for real league data. The browser keeps an
 * in-memory copy for React rendering and sends record-level patches to
 * `/api/state/commit` after service mutations. localStorage is reserved for
 * unsaved drafts and UI preferences owned by their own modules.
 */

import type {
  Announcement,
  CommunicationDelivery,
  CommunicationConsent,
  CommunicationMessage,
  CommunicationThread,
  CommunicationThreadMessage,
  Division,
  Game,
  GameArchive,
  GameAssignment,
  GameResult,
  GameRoster,
  GameScoringSession,
  LeagueConfig,
  LockerRoomMessage,
  Official,
  OfficialAssignment,
  PaymentIntent,
  Player,
  PlayerEligibility,
  Profile,
  Registration,
  Rink,
  RoleAssignment,
  RosterEntry,
  ScheduleSlot,
  ScoreEvent,
  StandingsRecord,
  Team,
  Tournament,
  TournamentSettings,
  TournamentType,
  Venue,
  Waiver,
  WaiverDocument,
} from '../models';
import {
  DEFAULT_LEAGUE,
  normalizeLeagueConfig,
} from '../models/league';

export interface AppDatabase {
  league: LeagueConfig;
  profiles: Profile[];
  tournamentTypes: TournamentType[];
  tournaments: Tournament[];
  divisions: Division[];
  teams: Team[];
  players: Player[];
  rosterEntries: RosterEntry[];
  roleAssignments: RoleAssignment[];
  gameAssignments: GameAssignment[];
  venues: Venue[];
  rinks: Rink[];
  scheduleSlots: ScheduleSlot[];
  games: Game[];
  gameScoringSessions: GameScoringSession[];
  scoreEvents: ScoreEvent[];
  gameResults: GameResult[];
  gameArchives: GameArchive[];
  standings: StandingsRecord[];
  communications: CommunicationMessage[];
  communicationThreads: CommunicationThread[];
  communicationThreadMessages: CommunicationThreadMessage[];
  communicationDeliveries: CommunicationDelivery[];
  communicationConsents: CommunicationConsent[];
  announcements: Announcement[];
  lockerRoomMessages: LockerRoomMessage[];
  registrations: Registration[];
  waiverDocuments: WaiverDocument[];
  waivers: Waiver[];
  paymentIntents: PaymentIntent[];
  gameRosters: GameRoster[];
  playerEligibility: PlayerEligibility[];
  officials: Official[];
  officialAssignments: OfficialAssignment[];
}

type CollectionKey = Exclude<{
  [K in keyof AppDatabase]: AppDatabase[K] extends Array<unknown> ? K : never
}[keyof AppDatabase], undefined>;

interface StatePatch {
  op: 'upsert' | 'delete';
  slice: CollectionKey | 'league';
  id: string;
  baseUpdatedAt?: string | null;
  record?: unknown;
}

interface BootstrapResponse {
  ok?: boolean;
  db?: AppDatabase;
  version?: number;
  error?: string;
}

interface CommitResponse {
  ok?: boolean;
  db?: AppDatabase;
  version?: number;
  error?: string;
  message?: string;
}

export const emptyDb = (): AppDatabase => ({
  league: { ...DEFAULT_LEAGUE },
  profiles: [],
  tournamentTypes: [],
  tournaments: [],
  divisions: [],
  teams: [],
  players: [],
  rosterEntries: [],
  roleAssignments: [],
  gameAssignments: [],
  venues: [],
  rinks: [],
  scheduleSlots: [],
  games: [],
  gameScoringSessions: [],
  scoreEvents: [],
  gameResults: [],
  gameArchives: [],
  standings: [],
  communications: [],
  communicationThreads: [],
  communicationThreadMessages: [],
  communicationDeliveries: [],
  communicationConsents: [],
  announcements: [],
  lockerRoomMessages: [],
  registrations: [],
  waiverDocuments: [],
  waivers: [],
  paymentIntents: [],
  gameRosters: [],
  playerEligibility: [],
  officials: [],
  officialAssignments: [],
});

const COLLECTION_KEYS: CollectionKey[] = [
  'profiles',
  'tournamentTypes',
  'tournaments',
  'divisions',
  'teams',
  'players',
  'rosterEntries',
  'roleAssignments',
  'gameAssignments',
  'venues',
  'rinks',
  'scheduleSlots',
  'games',
  'gameScoringSessions',
  'scoreEvents',
  'gameResults',
  'gameArchives',
  'standings',
  'communications',
  'communicationThreads',
  'communicationThreadMessages',
  'communicationDeliveries',
  'communicationConsents',
  'announcements',
  'lockerRoomMessages',
  'registrations',
  'waiverDocuments',
  'waivers',
  'paymentIntents',
  'gameRosters',
  'playerEligibility',
  'officials',
  'officialAssignments',
];

let _db: AppDatabase = emptyDb();
let cloudEnabled = false;
let cloudVersion = 0;
let pendingPatches: StatePatch[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;
let localMutationRevision = 0;

const listeners = new Set<() => void>();

export const getDb = (): AppDatabase => _db;

export const setDb = (next: AppDatabase, options: { sync?: boolean } = {}): void => {
  const prev = _db;
  _db = migrate({ ...emptyDb(), ...next });
  notify();
  if (options.sync) {
    localMutationRevision += 1;
    enqueuePatches(diffDb(prev, _db));
  }
};

export const updateDb = (updater: (db: AppDatabase) => AppDatabase): void => {
  const prev = _db;
  _db = migrate({ ...emptyDb(), ...updater(_db) });
  localMutationRevision += 1;
  notify();
  enqueuePatches(diffDb(prev, _db));
};

export const subscribeDb = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const hydrateDb = (): AppDatabase | null => null;

export async function initializeCloudStore(fallback?: AppDatabase): Promise<void> {
  if (typeof window === 'undefined') {
    setDb(fallback ?? emptyDb());
    return;
  }
  try {
    const res = await fetch('/api/bootstrap', { credentials: 'same-origin' });
    const data = (await res.json()) as BootstrapResponse;
    if (!res.ok || !data.ok || !data.db) {
      throw new Error(data.error ?? `bootstrap failed: ${res.status}`);
    }
    cloudEnabled = true;
    cloudVersion = data.version ?? 0;
    setDb(data.db);
  } catch (err) {
    cloudEnabled = false;
    if (fallback) {
      setDb(fallback);
      console.warn('Cloud bootstrap failed; using local seed fallback only.', err);
      return;
    }
    throw err instanceof Error ? err : new Error('Cloud bootstrap failed');
  }
}

export function getCloudStoreStatus(): {
  enabled: boolean;
  version: number;
  pending: number;
  flushing: boolean;
} {
  return { enabled: cloudEnabled, version: cloudVersion, pending: pendingPatches.length, flushing };
}

export const resetDb = (next: AppDatabase): void => {
  _db = migrate({ ...emptyDb(), ...next });
  notify();
};

function notify() {
  listeners.forEach((fn) => fn());
}

function migrate(db: AppDatabase): AppDatabase {
  const renameRole = (r: string) => (r === 'commissioner' ? 'admin' : r);
  const legacyOrg = (db as unknown as { org?: Partial<LeagueConfig> }).org;
  const incomingLeague = db.league ?? legacyOrg;
  const league: LeagueConfig = normalizeLeagueConfig(incomingLeague);

  return {
    ...db,
    league,
    profiles: db.profiles.map((p) => ({
      ...p,
      defaultRole: renameRole(p.defaultRole) as typeof p.defaultRole,
      globalRoles: p.globalRoles.map((r) => renameRole(r) as typeof r),
      directoryStatus: p.directoryStatus ?? 'active',
      preferredContactMethod: p.preferredContactMethod ?? (p.email ? 'email' : p.phone ? 'sms' : 'in_app'),
      contactPreferences: p.contactPreferences ?? {},
      attributes: p.attributes ?? {},
      source: p.source ?? 'manual',
      tags: p.tags ?? [],
    })),
    roleAssignments: db.roleAssignments.map((a) => ({
      ...a,
      role: renameRole(a.role) as typeof a.role,
    })),
    tournaments: db.tournaments.map((t) => ({
      ...t,
      format: t.format ?? 'tournament',
      settings: normalizeCompetitionSettings(t.settings, t.format ?? 'tournament'),
    })),
    communicationThreads: db.communicationThreads ?? [],
    communicationThreadMessages: db.communicationThreadMessages ?? [],
    communicationDeliveries: db.communicationDeliveries ?? [],
    communicationConsents: db.communicationConsents ?? [],
    gameScoringSessions: db.gameScoringSessions ?? [],
    gameArchives: db.gameArchives ?? [],
    gameRosters: db.gameRosters.map((r) => ({
      ...r,
      attendanceByPlayerId: r.attendanceByPlayerId ?? {},
      attendanceNotesByPlayerId: r.attendanceNotesByPlayerId ?? {},
    })),
  };
}

function normalizeCompetitionSettings(
  settings: TournamentSettings,
  format: 'tournament' | 'season',
): TournamentSettings {
  const normalized: TournamentSettings = {
    ...settings,
    scoring: normalizeScoringSettings(settings.scoring, format),
    modules: {
      proShop: settings.modules?.proShop ?? false,
      concessions: settings.modules?.concessions ?? false,
      locker: settings.modules?.locker ?? true,
      gamification: settings.modules?.gamification ?? false,
    },
  };
  if (format !== 'season') return normalized;
  normalized.season = {
    weekdays: settings.season?.weekdays ?? [],
    firstPuckTime: settings.season?.firstPuckTime ?? '19:00',
    gamesPerRinkPerNight: settings.season?.gamesPerRinkPerNight ?? 3,
    regularSeasonEndsAt: settings.season?.regularSeasonEndsAt,
    blackoutDates: settings.season?.blackoutDates ?? [],
    rosterIntake: settings.season?.rosterIntake ?? 'later',
    registration: {
      enabled: settings.season?.registration?.enabled ?? false,
      intakeType: settings.season?.registration?.intakeType ?? 'individual',
      opensAt: settings.season?.registration?.opensAt,
      closesAt: settings.season?.registration?.closesAt,
      feeCents: settings.season?.registration?.feeCents ?? 5000,
      requireWaiver: settings.season?.registration?.requireWaiver ?? true,
      autoApprove: settings.season?.registration?.autoApprove ?? false,
    },
    rosterLockedAt: settings.season?.rosterLockedAt,
    halfSplit: settings.season?.halfSplit ?? false,
  };
  return normalized;
}

function normalizeScoringSettings(
  scoring: TournamentSettings['scoring'],
  format: 'tournament' | 'season',
): TournamentSettings['scoring'] {
  const periodCount = scoring.periodCount ?? (format === 'season' ? 3 : undefined);
  const periodLengthMinutes = scoring.periodLengthMinutes ?? (format === 'season' ? 12 : undefined);
  const gameLengthMinutes =
    scoring.gameLengthMinutes ??
    (periodCount && periodLengthMinutes ? periodCount * periodLengthMinutes : 12);
  return {
    ...scoring,
    periodCount,
    periodLengthMinutes,
    gameLengthMinutes,
    overtimeFormat: scoring.overtimeFormat ?? 'straight_shootout',
    overtimeMinutes: scoring.overtimeMinutes ?? 5,
    overtimeSkaters: scoring.overtimeSkaters ?? 3,
    shootoutRoundCount: scoring.shootoutRoundCount ?? 3,
    pointsRegulationWin: scoring.pointsRegulationWin ?? 2,
    pointsOvertimeWin: scoring.pointsOvertimeWin ?? 2,
    pointsShootoutWin: scoring.pointsShootoutWin ?? 2,
    pointsOvertimeLoss: scoring.pointsOvertimeLoss ?? 1,
    pointsShootoutLoss: scoring.pointsShootoutLoss ?? 1,
    pointsTie: scoring.pointsTie ?? 1,
  };
}

function enqueuePatches(patches: StatePatch[]): void {
  if (!cloudEnabled || typeof window === 'undefined' || patches.length === 0) return;
  pendingPatches.push(...patches);
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushPatches();
  }, 250);
}

async function flushPatches(): Promise<void> {
  if (flushing || pendingPatches.length === 0) return;
  flushing = true;
  const patches = compactPatches(pendingPatches);
  const revisionAtFlushStart = localMutationRevision;
  pendingPatches = [];
  try {
    const res = await fetch('/api/state/commit', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ patches }),
    });
    const data = (await res.json().catch(() => ({}))) as CommitResponse;
    if (!res.ok || data.ok === false) {
      if (data.db) applyServerDbIfCurrent(data.db, revisionAtFlushStart);
      throw new Error(data.message ?? data.error ?? `commit failed: ${res.status}`);
    }
    if (data.db) applyServerDbIfCurrent(data.db, revisionAtFlushStart);
    cloudVersion = data.version ?? cloudVersion;
  } catch (err) {
    pendingPatches = [...patches, ...pendingPatches];
    console.error('Cloud state commit failed. Changes remain in memory and will retry on the next edit.', err);
  } finally {
    flushing = false;
  }
}

function applyServerDbIfCurrent(db: AppDatabase, revisionAtFlushStart: number): void {
  if (localMutationRevision !== revisionAtFlushStart || pendingPatches.length > 0) return;
  setDb(db);
}

function diffDb(prev: AppDatabase, next: AppDatabase): StatePatch[] {
  const patches: StatePatch[] = [];
  if (stableJson(prev.league) !== stableJson(next.league)) {
    patches.push({ op: 'upsert', slice: 'league', id: 'league', record: next.league });
  }

  for (const key of COLLECTION_KEYS) {
    const beforeRows = new Map((prev[key] as Array<{ id: string; updatedAt?: string }>).map((row) => [row.id, row]));
    const afterRows = new Map((next[key] as Array<{ id: string; updatedAt?: string }>).map((row) => [row.id, row]));

    for (const [id, after] of afterRows) {
      const before = beforeRows.get(id);
      if (!before || stableJson(before) !== stableJson(after)) {
        patches.push({
          op: 'upsert',
          slice: key,
          id,
          baseUpdatedAt: before?.updatedAt ?? null,
          record: after,
        });
      }
    }

    for (const [id, before] of beforeRows) {
      if (!afterRows.has(id)) {
        patches.push({
          op: 'delete',
          slice: key,
          id,
          baseUpdatedAt: before.updatedAt ?? null,
        });
      }
    }
  }

  return patches;
}

function compactPatches(patches: StatePatch[]): StatePatch[] {
  const byKey = new Map<string, StatePatch>();
  for (const patch of patches) {
    const key = `${patch.slice}:${patch.id}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, patch);
      continue;
    }
    byKey.set(key, {
      ...patch,
      baseUpdatedAt: Object.prototype.hasOwnProperty.call(existing, 'baseUpdatedAt')
        ? existing.baseUpdatedAt
        : patch.baseUpdatedAt ?? null,
    });
  }
  return Array.from(byKey.values());
}

function stableJson(value: unknown): string {
  return JSON.stringify(value);
}

export const __storeTestHooks = {
  compactPatches,
};
